import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  IndianRupee, ShoppingBag, Package, Users, TrendingUp, Clock, CheckCircle2, XCircle, AlertTriangle, PackageX,
  RefreshCw, ArrowUpRight, Settings, Boxes,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inr = (n) => "₹" + (n || 0).toLocaleString("en-IN");
const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good Morning," : h < 17 ? "Good Afternoon," : "Good Evening,"; };
const initials = (n = "") => n.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";

// pastel card tints per theme colour: primary blue, secondary orange, tertiary green
const TINT = {
  green: { card: "from-green-50 to-white border-green-100", chip: "bg-green-100 text-green-600" },
  blue: { card: "from-blue-50 to-white border-blue-100", chip: "bg-blue-100 text-blue-600" },
  orange: { card: "from-orange-50 to-white border-orange-100", chip: "bg-orange-100 text-orange-600" },
};

export default function Overview() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [store, setStore] = useState(null);
  const [spin, setSpin] = useState(false);

  const load = useCallback(async () => {
    setSpin(true);
    try {
      const [o, s] = await Promise.all([api.get("/vendor/overview"), api.get("/vendor/store")]);
      setD(o.data); setStore(s.data);
    } finally { setSpin(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="text-slate-500 text-sm">Loading…</div>;

  const cards = [
    { label: "Today's Sales", value: inr(d.today_sales), sub: "since midnight", icon: IndianRupee, color: "green" },
    { label: "Total Revenue", value: inr(d.revenue), sub: "all time", icon: TrendingUp, color: "blue" },
    { label: "Orders", value: d.orders_count, sub: `${d.pending_orders} pending`, icon: ShoppingBag, color: "orange" },
    { label: "Customers", value: d.customers_count, sub: "unique buyers", icon: Users, color: "blue" },
  ];
  const stats = [
    { label: "Pending", value: d.pending_orders, icon: Clock, dot: "bg-orange-500" },
    { label: "Completed", value: d.completed_orders, icon: CheckCircle2, dot: "bg-green-500" },
    { label: "Cancelled", value: d.cancelled_orders, icon: XCircle, dot: "bg-red-500" },
    { label: "Products", value: d.products_count, icon: Package, dot: "bg-blue-600" },
    { label: "Low Stock", value: d.low_stock, icon: AlertTriangle, dot: "bg-orange-500" },
    { label: "Out of Stock", value: d.out_of_stock, icon: PackageX, dot: "bg-red-500" },
  ];
  const quick = [
    { to: "/vendor/products", icon: Package, label: "Products" },
    { to: "/vendor/orders", icon: ShoppingBag, label: "Orders" },
    { to: "/vendor/inventory", icon: Boxes, label: "Inventory" },
    { to: "/vendor/settings", icon: Settings, label: "Settings" },
  ];

  return (
    <div className="space-y-6" data-testid="vendor-overview">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-[20px] font-bold text-neutral-900">Dashboard</h1>
          <p className="text-slate-500 text-[13px]">Overview of your store sales, orders and recent activity.</p>
        </div>
        <button onClick={load} data-testid="vendor-refresh"
          className="flex items-center gap-2 h-9 px-4 rounded-lg border border-slate-200 bg-white text-[13px] font-semibold text-neutral-800 hover:border-blue-300 hover:text-blue-600 transition-colors">
          <RefreshCw size={14} className={spin ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 space-y-4 shadow-sm">
        {/* Banner */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-xl bg-gradient-to-r from-black via-neutral-950 to-blue-950 text-white px-6 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="absolute -right-10 -top-16 w-64 h-64 rounded-full bg-blue-600/25 blur-3xl" />
          <div className="relative">
            <div className="text-xs text-slate-300">{greeting()}</div>
            <div className="font-display text-2xl font-bold mt-0.5">{store?.name || user?.name} <span className="inline-block vx-wave">👋</span></div>
            <div className="text-xs text-slate-300 mt-1">Here&apos;s what&apos;s happening across your store today.</div>
            <div className="mt-3 flex items-center gap-2 text-[13px] font-semibold text-green-400"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />{d.orders_count} total orders</div>
          </div>
          <div className="relative flex items-center gap-1 sm:border-l sm:border-white/15 sm:pl-5">
            {quick.map((q) => (
              <Link key={q.to} to={q.to} className="flex flex-col items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium text-slate-200 hover:bg-white/10 hover:text-orange-400 transition-colors">
                <q.icon size={18} /> {q.label}
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {cards.map((c, i) => (
            <motion.div key={c.label} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * i }}
              className={`rounded-xl border bg-gradient-to-br ${TINT[c.color].card} p-4 hover:-translate-y-0.5 hover:shadow-md transition-all`}>
              <div className="flex items-start justify-between">
                <div className="text-[13px] text-slate-500">{c.label}</div>
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${TINT[c.color].chip}`}><c.icon size={16} /></span>
              </div>
              <div className="text-[26px] font-bold text-neutral-900 leading-tight mt-1">{c.value}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{c.sub}</div>
            </motion.div>
          ))}
        </div>

        {/* Status pills */}
        <div className="flex flex-wrap gap-2.5">
          {stats.map((s) => (
            <div key={s.label} className="flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 bg-white text-[13px] text-neutral-800">
              <span className={`w-2 h-2 rounded-full ${s.dot}`} /> {s.label} <span className="font-bold">{s.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent orders */}
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
            <div>
              <h2 className="font-bold text-neutral-900 text-[15px]">Recent Orders</h2>
              <p className="text-[12px] text-slate-500">Most recently placed orders</p>
            </div>
            <Link to="/vendor/orders" className="flex items-center gap-1 text-[12px] font-semibold text-blue-600 hover:underline">View all <ArrowUpRight size={13} /></Link>
          </div>
          {d.recent_orders.length === 0 ? (
            <p className="text-slate-500 text-sm p-6">No orders yet.</p>
          ) : (
            <div>
              {d.recent_orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between px-5 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-9 h-9 rounded-full bg-orange-100 text-orange-600 text-xs font-bold flex items-center justify-center shrink-0">{initials(o.customer?.name)}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-neutral-900 truncate">{o.customer?.name}</div>
                      <div className="text-[12px] text-slate-500 font-mono">{o.order_number}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <StatusBadge status={o.status} />
                    <div className="text-[12px] text-slate-500 mt-1 font-semibold">{inr(o.total)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Revenue */}
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm">
          <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
            <div>
              <h2 className="font-bold text-neutral-900 text-[15px]">Revenue Trend</h2>
              <p className="text-[12px] text-slate-500">Revenue over recent days</p>
            </div>
            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-green-50 text-green-700 border border-green-100">{inr(d.revenue)} total</span>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={d.trend}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, color: "#0a0a0a", fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2.5} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top products */}
      <div className="rounded-xl bg-white border border-slate-200 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-bold text-neutral-900 text-[15px]">Top Products</h2>
          <p className="text-[12px] text-slate-500">Best sellers by units sold</p>
        </div>
        {(!d.top_products || d.top_products.length === 0) ? <p className="text-slate-500 text-sm p-6">No sales yet.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 px-5 py-2">
            {d.top_products.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 border-b border-slate-100">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 text-xs flex items-center justify-center font-bold shrink-0">{i + 1}</span>
                  <span className="text-sm text-neutral-900 truncate">{p.name}</span>
                </div>
                <span className="text-xs font-semibold text-green-600 shrink-0">{p.qty} sold</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function StatusBadge({ status, label }) {
  const map = {
    pending: "bg-orange-50 text-orange-700 border-orange-200",
    confirmed: "bg-blue-50 text-blue-700 border-blue-200",
    processing: "bg-blue-50 text-blue-700 border-blue-200",
    packed: "bg-blue-50 text-blue-700 border-blue-200",
    shipped: "bg-blue-50 text-blue-700 border-blue-200",
    out_for_delivery: "bg-blue-50 text-blue-700 border-blue-200",
    delivered: "bg-green-50 text-green-700 border-green-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
    active: "bg-green-50 text-green-700 border-green-200",
    suspended: "bg-red-50 text-red-700 border-red-200",
  };
  return <span className={`inline-block text-[11px] px-2 py-0.5 rounded-md border font-medium capitalize ${map[status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{(label ?? status ?? "").replace(/_/g, " ")}</span>;
}
